import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Camera, FileText, Mail, MessageSquare, Sparkles, History, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import UploadZone from '@/components/UploadZone';
import QuickActionCard from '@/components/QuickActionCard';

export default function Home({ darkMode, setDarkMode }) {
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleFileUpload = async (fileData) => {
    setIsProcessing(true);
    
    try {
      // fileData now contains the server response with file metadata
      const mockId = Date.now();
      const analysisData = {
        id: mockId,
        type: 'document',
        fileName: fileData.original_filename,
        fileUrl: `${process.env.REACT_APP_BACKEND_URL}${fileData.file_url}`,
        fileId: fileData.id,
        contentType: fileData.content_type,
        extractedText: fileData.extracted_text,
        timestamp: new Date().toISOString(),
        content: fileData.extracted_text || 'Uploaded file'
      };
      
      // Save to localStorage for prototype
      const existingHistory = JSON.parse(localStorage.getItem('nowwhat_history') || '[]');
      existingHistory.unshift(analysisData);
      localStorage.setItem('nowwhat_history', JSON.stringify(existingHistory));
      
      setIsProcessing(false);
      toast.success('File uploaded and processed successfully!');
      navigate(`/analysis/${mockId}`);
    } catch (error) {
      setIsProcessing(false);
      toast.error('Failed to process file');
      console.error('File processing error:', error);
    }
  };

  const handleTextSubmit = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter some text');
      return;
    }
    
    setIsProcessing(true);
    
    // Simulate processing
    setTimeout(() => {
      const mockId = Date.now();
      const analysisData = {
        id: mockId,
        type: 'text',
        content: inputText,
        timestamp: new Date().toISOString()
      };
      
      // Save to localStorage
      const existingHistory = JSON.parse(localStorage.getItem('nowwhat_history') || '[]');
      existingHistory.unshift(analysisData);
      localStorage.setItem('nowwhat_history', JSON.stringify(existingHistory));
      
      setIsProcessing(false);
      setInputText('');
      toast.success('Text analyzed successfully!');
      navigate(`/analysis/${mockId}`);
    }, 1500);
  };

  const quickActions = [
    {
      icon: FileText,
      title: 'Bills & Invoices',
      description: 'Understand charges and due dates',
      color: 'primary'
    },
    {
      icon: Mail,
      title: 'Email & Letters',
      description: 'Summarize and draft replies',
      color: 'secondary'
    },
    {
      icon: MessageSquare,
      title: 'Messages',
      description: 'Know how to respond',
      color: 'accent'
    },
    {
      icon: Camera,
      title: 'Screenshots',
      description: 'Extract and organize info',
      color: 'primary'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">NOWWHAT</h1>
              <p className="text-xs text-muted-foreground">One clear next step</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="rounded-full"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/history')}
              className="rounded-full"
            >
              <History className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-slide-up">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 leading-tight">
            Send it.
            <br />
            <span className="text-gradient-primary">Understand it.</span>
            <br />
            Know what to do next.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Upload anything confusing or overwhelming. Get one clear explanation and one next action.
          </p>
        </div>

        {/* Upload Zone */}
        <Card className="mb-8 p-8 shadow-lg border-2 border-dashed border-border hover:border-primary transition-smooth">
          <UploadZone onFileUpload={handleFileUpload} isProcessing={isProcessing} />
        </Card>

        {/* Text Input */}
        <Card className="mb-12 p-6 shadow-md">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Or paste/type your question
              </label>
              <Textarea
                placeholder="Paste an email, message, or ask a question..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="min-h-[120px] resize-none"
                disabled={isProcessing}
              />
            </div>
            <Button
              onClick={handleTextSubmit}
              disabled={isProcessing || !inputText.trim()}
              className="w-full bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary"
              size="lg"
            >
              {isProcessing ? (
                <>
                  <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Analyze Now
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-foreground mb-6">What can NOWWHAT help with?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </div>
        </div>

        {/* How It Works */}
        <Card className="p-8 bg-gradient-subtle border-0 shadow-lg">
          <h3 className="text-2xl font-semibold text-foreground mb-6 text-center">How it works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-primary" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">1. Send it</h4>
              <p className="text-sm text-muted-foreground">
                Upload a screenshot, photo, paste text, or ask a question
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-secondary" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">2. Understand it</h4>
              <p className="text-sm text-muted-foreground">
                Get a clear explanation in plain language
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-accent" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">3. Take action</h4>
              <p className="text-sm text-muted-foreground">
                Tap the recommended next step and get it done
              </p>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
